import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  FaTimes, 
  FaCheck, 
  FaArrowLeft, 
  FaArrowRight,
  FaFileAlt,
  FaEdit,
  FaEye,
  FaSave,
  FaUndo
} from 'react-icons/fa';
import Button from '../../components/buttons/Button';
import { parseReportSections } from '../../ReportList/parseReportSections';
import { userService } from '../../services';
import StepReportSelector from './steps/StepReportSelector';
import StepSectionEditor from './steps/StepSectionEditor';
import StepPreview from './steps/StepPreview';
import StepExport from './steps/StepExport';
import CustomInstructionsPanel from './CustomInstructionsPanel';
import './TemplateBuilderWizard.css';
import { createLogger } from '../../services/Debug';
const STEPS = [
  { id: 1, title: 'Choisir un rapport', icon: FaFileAlt },
  { id: 2, title: 'Éditer les sections', icon: FaEdit },
  { id: 3, title: 'Aperçu', icon: FaEye },
  { id: 4, title: 'Télécharger', icon: FaSave }
];

function TemplateBuilderWizard({ isOpen, onClose, onTemplateCreated }) {
  const log = createLogger('TemplateBuilderWizard');
  // Main state
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedReport, setSelectedReport] = useState(null);
  const [sections, setSections] = useState([]);
  const [editedSections, setEditedSections] = useState([]);
  const [templateConfig, setTemplateConfig] = useState({
    name: '',
    reportType: '',
    includeLogo: true,
    pageLayout: 'standard'
  });
  const [logoUrl, setLogoUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [showCustomInstructions, setShowCustomInstructions] = useState(false);

  // Load user logo on mount
  useEffect(() => {
    if (!isOpen) return;
    
    const loadLogo = async () => {
      try {
        const blob = await userService.getUserLogo();
        if (blob && blob.size > 0) {
          const url = window.URL.createObjectURL(blob);
          setLogoUrl(url);
          return () => window.URL.revokeObjectURL(url);
        }
      } catch (e) {
        // No logo available
        setLogoUrl('');
      }
    };
    
    const cleanup = loadLogo();
    return () => {
      if (cleanup && typeof cleanup === 'function') cleanup();
    };
  }, [isOpen]);

  // Parse sections when report is selected
  useEffect(() => {
    if (selectedReport) {
      const text = selectedReport.text || selectedReport.content || '';
      const parsed = parseReportSections(text);
      setSections(parsed);
      setEditedSections(parsed.map((s, idx) => {
        const format = detectSectionFormat(s);
        return {
          ...s,
          id: `section-${idx}`,
          format: format,
          isVisible: true,
          subtitle: '',
          alignment: format === 'table' ? 'center' : 'left'
        };
      }));
      
      // Auto-set template name based on report type
      setTemplateConfig(prev => ({
        ...prev,
        name: selectedReport.reportType || 'Mon Template',
        reportType: selectedReport.reportType || ''
      }));
    }
  }, [selectedReport]);

  // Detect section format (list, table, paragraph, inline)
  const detectSectionFormat = (section) => {
    const content = section.content || '';
    
    // Check for table format
    if (content.includes('---')) {
      return 'table';
    }
    
    // Check for inline format (Title: content on same line)
    const hasNewlines = content.includes('\n');
    if (!hasNewlines && content.length > 0 && content.length < 100) {
      return 'inline';
    }
    
    // Default to paragraph - never auto-detect as list
    return 'paragraph';
  };

  // Navigation handlers
  const canProceed = useCallback(() => {
    switch (currentStep) {
      case 1:
        return selectedReport !== null;
      case 2:
        return editedSections.length > 0;
      case 3:
        return true;
      case 4:
        return false; // Last step
      default:
        return false;
    }
  }, [currentStep, selectedReport, editedSections]);

  const handleNext = () => {
    if (canProceed()) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
      setError('');
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const handleReset = () => {
    if (window.confirm('Voulez-vous réinitialiser le processus de création ?')) {
      setCurrentStep(1);
      setSelectedReport(null);
      setSections([]);
      setEditedSections([]);
      setTemplateConfig({
        name: '',
        reportType: '',
        includeLogo: true,
        pageLayout: 'standard'
      });
      setError('');
      setHasDownloaded(false);
    }
  };

  const handleStepClick = (stepId) => {
    // Allow direct navigation to completed steps only
    if (stepId < currentStep) {
      setCurrentStep(stepId);
      setError('');
    } else if (stepId === currentStep + 1 && canProceed()) {
      // Allow clicking next step if current step is complete
      setCurrentStep(stepId);
      setError('');
    }
  };

  const isStepAccessible = (stepId) => {
    // All steps are accessible once a report is selected
    if (selectedReport !== null) return true;
    // Otherwise only the first step is accessible
    return stepId === 1;
  };

  // Section editing handlers
  const handleSectionUpdate = (sectionId, updates) => {
    setEditedSections(prev => prev.map(s => 
      s.id === sectionId ? { ...s, ...updates } : s
    ));
  };

  const handleSectionReorder = (fromIndex, toIndex) => {
    const newSections = [...editedSections];
    const [removed] = newSections.splice(fromIndex, 1);
    newSections.splice(toIndex, 0, removed);
    setEditedSections(newSections);
  };

  const handleSectionDelete = (sectionId) => {
    setEditedSections(prev => prev.filter(s => s.id !== sectionId));
  };

  const handleSectionAdd = (format = 'paragraph') => {
    const newId = Date.now();
    const newSection = {
      id: `section-${newId}`,
      title: format === 'table' ? 'Tableau' : format === 'list' ? 'Liste' : format === 'inline' ? 'Champ' : 'Nouvelle section',
      content: '',
      subtitle: '',
      format: format,
      isVisible: true,
      alignment: format === 'table' ? 'center' : 'left'
    };
    setEditedSections(prev => [...prev, newSection]);
    return newId;
  };

  // Export handler
  const handleExport = async (exportType) => {
    setIsLoading(true);
    setError('');
    
    try {
      const docxBlob = await generateDocx();
      
      if (exportType === 'download') {
        downloadBlob(docxBlob, `${templateConfig.name || 'template'}.docx`);
        setHasDownloaded(true);
      } else if (exportType === 'save') {
        // Save as template (would need API endpoint)
        if (onTemplateCreated) {
          onTemplateCreated(docxBlob, templateConfig);
        }
      }
      
      // Success - could show notification
      return true;
    } catch (e) {
      setError('Erreur lors de l\'export du template');
      log.error('Export error:', e);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Generate DOCX document
  const generateDocx = async () => {
    const { Document, Packer, Paragraph, TextRun, AlignmentType } = await import('docx');
    
    const docParagraphs = [];
    
    // Add logo if enabled
    if (templateConfig.includeLogo) {
      docParagraphs.push(new Paragraph({
        alignment: AlignmentType.LEFT,
        children: [
          new TextRun('{#logoSection}'),
          new TextRun('{%logo}'),
          new TextRun('{/logoSection}')
        ]
      }));
      docParagraphs.push(new Paragraph('')); // Empty line
    }
    
    // Process each section
    editedSections.filter(s => s.isVisible).forEach(section => {
      const titleSlug = toSlug(section.title);
      const alignment = section.alignment || (section.format === 'table' ? 'center' : 'left');
      const alignmentType = alignment === 'center' ? AlignmentType.CENTER : alignment === 'right' ? AlignmentType.RIGHT : AlignmentType.LEFT;
      
      switch (section.format) {
        case 'inline':
          // Title: {content}
          docParagraphs.push(new Paragraph({
            alignment: alignmentType,
            children: [
              new TextRun({ text: `${section.title}: `, bold: true }),
              new TextRun(`{${titleSlug}}`)
            ]
          }));
          break;
          
        case 'table':
          // Table format
          docParagraphs.push(new Paragraph({
            alignment: alignmentType,
            children: [new TextRun(`{@tableau${toPascalCase(section.title)}}`)]
          }));
          if (section.content) {
            section.content.split('\n').forEach(line => {
              if (line.trim()) {
                docParagraphs.push(new Paragraph(line.trim()));
              }
            });
          }
          break;
          
        case 'list':
          // List format
          docParagraphs.push(new Paragraph({
            alignment: alignmentType,
            children: [new TextRun({ text: section.title, bold: true })]
          }));
          if (section.subtitle) {
            docParagraphs.push(new Paragraph({
              alignment: alignmentType,
              children: [new TextRun({ text: section.subtitle, italics: true })]
            }));
          }
          docParagraphs.push(new Paragraph({
            alignment: alignmentType,
            children: [new TextRun(`{#${titleSlug}}`)]
          }));
          docParagraphs.push(new Paragraph({
            alignment: alignmentType,
            children: [new TextRun(' - {}')]
          }));
          docParagraphs.push(new Paragraph({
            alignment: alignmentType,
            children: [new TextRun(`{/${titleSlug}}`)]
          }));
          break;
          
        case 'paragraph':
        default:
          // Paragraph format
          docParagraphs.push(new Paragraph({
            alignment: alignmentType,
            children: [new TextRun(`{#${titleSlug}Section}`)]
          }));
          docParagraphs.push(new Paragraph({
            alignment: alignmentType,
            children: [new TextRun({ text: section.title, bold: true })]
          }));
          if (section.subtitle) {
            docParagraphs.push(new Paragraph({
              alignment: alignmentType,
              children: [new TextRun({ text: section.subtitle, italics: true })]
            }));
          }
          docParagraphs.push(new Paragraph({
            alignment: alignmentType,
            children: [new TextRun('{#contenu}{.}')]
          }));
          docParagraphs.push(new Paragraph({
            alignment: alignmentType,
            children: [new TextRun(`{/contenu}{/${titleSlug}Section}`)]
          }));
          break;
      }
      
      docParagraphs.push(new Paragraph('')); // Empty line between sections
    });
    
    // Always add structured data image section if present
    if (selectedReport?.structuredData && Object.keys(selectedReport.structuredData).length > 0) {
      docParagraphs.push(new Paragraph('')); // Empty line before image section
      docParagraphs.push(new Paragraph('{#imageSection}'));
      docParagraphs.push(new Paragraph({
        children: [new TextRun({ text: 'Schéma cinétique', bold: true })]
      }));
      docParagraphs.push(new Paragraph('{%image}'));
      docParagraphs.push(new Paragraph('{/imageSection}'));
    }
    
    const doc = new Document({
      sections: [{
        properties: {},
        children: docParagraphs
      }]
    });
    
    return await Packer.toBlob(doc);
  };

  // Utility functions
  const toSlug = (text) => {
    return (text || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, '')
      .replace(/^(.)/, m => m.toLowerCase());
  };

  const toPascalCase = (text) => {
    return (text || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, ' ')
      .trim()
      .split(/\s+/)
      .map(s => s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : '')
      .join('');
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Handle close without auto-download
  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="tbw-overlay" onClick={handleClose}>
      <div className="tbw-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="tbw-header">
          <h2>Créer un template</h2>
          <button className="tbw-close" onClick={handleClose} aria-label="Fermer">
            <FaTimes size={20} />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="tbw-steps">
          {STEPS.map(step => {
            const Icon = step.icon;
            const accessible = isStepAccessible(step.id);
            return (
              <div
                key={step.id}
                className={`tbw-step ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''} ${!accessible ? 'disabled' : ''}`}
                onClick={() => accessible && handleStepClick(step.id)}
              >
                <div className="tbw-step-number">
                  {currentStep > step.id ? <FaCheck size={12} /> : <Icon size={16} />}
                </div>
                <div className="tbw-step-title">{step.title}</div>
              </div>
            );
          })}
          <div 
            className="tbw-step-progress" 
            style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="tbw-content">
          {error && (
            <div className="tbw-error">
              {error}
            </div>
          )}

          {currentStep === 1 && (
            <StepReportSelector
              onSelect={setSelectedReport}
              selected={selectedReport}
            />
          )}

          {currentStep === 2 && (
            <StepSectionEditor
              sections={editedSections}
              onSectionUpdate={handleSectionUpdate}
              onSectionReorder={handleSectionReorder}
              onSectionDelete={handleSectionDelete}
              onSectionAdd={handleSectionAdd}
            />
          )}

          {currentStep === 3 && (
            <StepPreview
              sections={editedSections}
              templateConfig={templateConfig}
              onConfigChange={setTemplateConfig}
              logoUrl={logoUrl}
            />
          )}

          {currentStep === 4 && (
            <StepExport
              templateConfig={templateConfig}
              onExport={handleExport}
              isLoading={isLoading}
              sections={editedSections}
              onShowCustomInstructions={() => setShowCustomInstructions(true)}
            />
          )}
        </div>

        {/* Footer */}
        <div className="tbw-footer">
          <div className="tbw-footer-left">
            <Button
              variant="secondary"
              icon={FaArrowLeft}
              onClick={handlePrevious}
              disabled={currentStep === 1}
            >
              Précédent
            </Button>
            <Button
              variant="danger"
              icon={FaUndo}
              onClick={handleReset}
              size="small"
            >
              Réinitialiser
            </Button>
          </div>

          <div className="tbw-footer-center">
            Étape {currentStep} sur {STEPS.length}
          </div>

          {currentStep < STEPS.length ? (
            <Button
              variant="primary"
              icon={FaArrowRight}
              onClick={handleNext}
              disabled={!canProceed()}
            >
              Suivant
            </Button>
          ) : (
            <Button
              variant="success"
              icon={FaCheck}
              onClick={handleClose}
            >
              Terminer
            </Button>
          )}
        </div>
      </div>
      
      {/* Custom Instructions Panel */}
      <CustomInstructionsPanel
        isOpen={showCustomInstructions}
        onClose={() => setShowCustomInstructions(false)}
        reportType={templateConfig.reportType}
        sections={editedSections}
        templateConfig={templateConfig}
      />
    </div>
  );
}

export default TemplateBuilderWizard;